import React, { useState, useEffect, useRef } from "react";
import Form, { useForm } from "./Form";
import Controls from "../../atoms/Controls";
import { getKvnrError } from "../../utils/kvnrValidator";
import {
  IconButton,
  Button,
  Grid,
  Modal,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogActions,
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import EditTable from "../../molecules/Editable";
import { v4 as uuidv4 } from "uuid";
import ReceiptDataEntryDialog from "./Receipt";
import {
  AddressDto,
  AddressDtoTypeEnum,
  ContactInfoDTO,
  CreatePatientDto,
  InsurerDto,
  PatientDto,
} from "../../api-client";
import useStore from "../../helpers/useStore";
import {
  useMasterTables,
  getTarifGroupIds as utilGetTarifGroupIds,
  getBlankoTarifGroupIds,
} from "../../stores/MasterTables";
import { observer } from "mobx-react";
import { DoctorContext } from "../../stores/Doctor/doctor.provider";
// import { notifySuccess } from "../../atoms/Notify";
import { Delete, Edit, AddCircleOutline } from "@mui/icons-material";
import { RxStoreContext } from "../../stores/Rx/rx.provider";
import notificationStore from "../../stores/Notification/notificationStore";
import { handleParseInput } from "../../helpers/parseInput";
import WarningDialog from "../../atoms/WarningDialog";
import { TopLevelPaper } from "../../themes/StyledComponents";
import FlatCard from "../../atoms/FlatCard";
import PatientAddress, { PatientAddressRef } from "./PatientAddress";
import { DataPrivacyPolicyStatus } from "../../types/DataPrivacyPolicyStatus";
import { mobiliTheme } from "../../themes/mobiliTheme";
import QuickAddRxDialog from "../PatientOverview/RxInfo/QuickAddRxDialog";
import { UserContext } from "../../stores/User/User.provider";
import { QuickRxData } from "../PatientOverview/RxInfo/RxOverView";
import { useCreateOrUpdatePatient, usePatient } from "../../stores/Patients";
import moment from "moment";
import { PatientCommands } from "../../stores/Patients/patient.commands";
import { useNavigate } from "react-router-dom";
import { PatientsContext } from "../../stores/Patients/patients.provider";
import { useCurrentLocation } from "../../stores/User";
import CopaymentExemptionForm, {
  CopaymentExemptionFormValue,
} from "./CopaymentExemptionForm";
import { usePrescribedTherapyOptions } from "../../stores/MasterTables/masterTables.queries";
import { filterPrescribedTherapyOptions } from "../../helpers/prescribedTherapyMatch";

const cardStyles = {
  card1: {
    // backgroundColor: "#ffcccc",
    margin: "10px",
    height: "100px",
  },
};

const FlatCardGridItem = ({ children }: any) => (
  <Grid item xs={12} md={6}>
    {" "}
    {/*  md={4} lg={3} xl={2}*/}
    {children}
  </Grid>
);

export interface SelectedRow {
  therapy?: string;
  id: any;
  therapyId?: any;
  frequency?: any;
  // Add other properties if applicable
}

interface PatientPageProps {
  isEditing?: boolean;
  selectedPatientId?: number;
  setActiveTab?: any;
  useDialog?: boolean;
  openPatientDialog?: boolean;
  onClosePatientDialog?: any;
}

type Optional<T> = {
  [P in keyof T]?: T[P];
};

export interface AddressDtoValidation extends Omit<AddressDto, "contactInfo"> {
  id: string;
  contactInfo: Optional<ContactInfoDTO>;
}
// Define a type for updatedValues
type UpdatedValuesType = {
  rxs: any[]; // Replace 'any[]' with the actual type of 'rxs'
  // Add other properties as needed
  addresses: AddressDto[];
};

const PatientPage: React.FC<PatientPageProps> = observer(
  ({
    isEditing,
    selectedPatientId,
    setActiveTab, // Receive the callback prop
    useDialog,
    openPatientDialog,
    onClosePatientDialog,
  }) => {
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState<SelectedRow | null>(null);
    const navigate = useNavigate();
    const patientsStore = useStore(PatientsContext);
    const { navigateToPatient } = PatientCommands(patientsStore);
    const currentLocation = useCurrentLocation();

    // Use React Query mutation instead of direct store method
    const createOrUpdatePatientMutation = useCreateOrUpdatePatient();

    const RxStore = useStore(RxStoreContext);
    const { deleteRx } = RxStore;
    const [selectedOption, setSelectedOption] = useState(""); // Initialize with an empty string
    const [loading, setLoading] = useState(false);
    const DoctorStore = useStore(DoctorContext);
    // Fetch RX data using the rxId from store
    const {
      data: patientData,
      isLoading: isRxLoading,
      error: rxError,
    } = usePatient(selectedPatientId!);
    // Initialize updatedValues with the expected type
    const [updatedValues, setUpdatedValues] =
      useState<UpdatedValuesType | null>({
        rxs: [],
        addresses: [],
      });
    const [addressesFormValidationErrors, setAddressesFormValidationErrors] =
      useState<(AddressDtoValidation | null)[]>();
    const [selectedInsurerType, setSelectedInsurerType] = useState("");
    const [selectedChipCard, setSelectedChipCard] = useState<string | null>(
      null,
    );

    const patientAddressRef = useRef<PatientAddressRef>(null);
    const [isQuickAdd, setIsQuickAdd] = useState(false);
    const [quickAddInputValue, setQuickAddInputValue] = useState("");
    const UserStore = useStore(UserContext);
    const { user } = UserStore;
    const [quickRxData, setQuickRxData] = useState<QuickRxData>({
      rxTypeData: { id: "", text: "" },
      selectedTarifGroup: "",
      rxAccount: "",
      tableData: [],
    });

    const [rxtableData, setrxTableData] = useState<any>([]);

    const handleCloseDialog = () => {
      setOpenDialog(false);
    };

    const handleDeleteRx = async (selectedRx: any) => {
      // console.log("Selected Rx:", selectedRx);

      try {
        let response;
        if (selectedRx.rxNumber) {
          response = await deleteRx(selectedRx.id);
          if (response) {
            const updatedRxs = values.rxs.filter(
              (rx: any) => rx.id !== selectedRx.id,
            );
            setValues((prevValues: any) => ({
              ...prevValues,
              rxs: updatedRxs,
            }));
            setrxTableData(updatedRxs);
          }
        } else {
          // If no rxNumber, remove the row from the table without deleting from the backend
          const updatedRxs = values.rxs.filter(
            (rx: any) => rx.id !== selectedRx.id,
          );
          setValues((prevValues: any) => ({
            ...prevValues,
            rxs: updatedRxs,
          }));
          setrxTableData(updatedRxs);
        }
      } catch (error) {
        console.error("Error deleting row:", error);
      }
    };
    const handleRowDoubleClick = (row: any) => {
      // console.log("xsssss", row);
      setSelectedRow(row);
      if (values.rxs.length == 0) {
        setValues((prevValues: any) => ({
          ...prevValues,
          rxs: [row],
        }));
      }
      setOpenDialog(true);
    };

    const [showFields, setShowFields] = useState(false);

    const handleToggleFields = (event: any) => {
      // console.log(
        // "Event.target",
        // event.target,
        // values.alternativeBillingLastname,
      // );
      setShowFields(event.target.value);
    };
    //Add PatientDto to check with David for id and dob
    const initialFValues = {
      salutation: "",
      lastName: "",
      firstName: "",
      dob: "",
      status: "",
      payer: {} as InsurerDto,
      tarifGroupId: "",
      insuranceNo: "",
      coPayment: false,
      street: "",
      country: "",
      zip: "",
      city: "",
      letterOfAddress: "",
      phone1: "",
      phone2: "",
      mobile: "",
      email: "",
      smsDesired: false,
      // info: "",
      // note: "",
      // personType: "",
      notes: [],
      rxs: [],
      alternativeBillingFirstnames: "",
      alternativeBillingLastname: "",
      alternativeBillingTel: "",
      alternativeBillingFax: "",
      alternativeBillingMobile: "",
      alternativeBillingAddressline1: "",
      alternativeBillingAddressline2: "",
      alternativeBillingCity: "",
      alternativeBillingZip: "",
      alternativeBillingEmail: "",
      treatmentBusinessName: "",
      treatmentTel: "",
      treatmentFax: "",
      treatmentMobile: "",
      treatmentAddressline1: "",
      treatmentAddressline2: "",
      treatmentCity: "",
      treatmentZip: "",
      treatmentEmail: "",
      addresses: [],
      copaymentExemptions: [] as CopaymentExemptionFormValue[],
      dataPrivacyPolicyStatus: DataPrivacyPolicyStatus.NOT_INFORMED,
      // Add more fields as needed
    } as CreatePatientDto;

    const fetchAndFormatPatientData = async (selectedPatientId: number) => {
      try {
        const response = patientData;
        if (!response) {
          throw new Error(`No data found for patient`);
        }
        // console.log("patientData", patientData);
        // Done by Nafees
        const normalizedCopaymentExemptions: CopaymentExemptionFormValue[] =
          response?.copaymentExemptions?.map((exemption: any) => ({
            id: exemption.id,
            startDate: exemption.startDate
              ? moment(exemption.startDate).format("YYYY-MM-DD")
              : "",
            endDate: exemption.endDate
              ? moment(exemption.endDate).format("YYYY-MM-DD")
              : "",
            documentId: exemption.documentId || exemption.document?.id || null,
            documentName:
              exemption.document?.name || exemption.document?.label || null,
          })) || [];
        const formattedValues = {
          ...response,
          dob: moment(response.dob).format("YYYY-MM-DD"),
          payer: response.payer,
          chipcard: response.payer?.ik,
          smsDesired: response?.smsDesired,
          dataPrivacyPolicyStatus:
            response?.dataPrivacyPolicyStatus ||
            DataPrivacyPolicyStatus.NOT_INFORMED,
          rxs: response.rxs?.filter((rx: any) => rx.deletedAt == null),
          addresses: response?.addresses,
          copaymentExemptions: normalizedCopaymentExemptions,
          // Set other properties from the response object as needed
        };
        // Set values to formattedValues
        setValues(formattedValues);

        // Set updatedValues to formattedValues to trigger the effect
        // setUpdatedValues(formattedValues);
        setSelectedInsurerType(formattedValues?.payer?.type!);

        if (response.addresses && response.addresses.length === 0) {
          handleAddAddress();
        }
      } catch (error) {
        // console.log("error", error);
      }
    };
    // Use a useEffect to watch for changes in updatedValues
    useEffect(() => {
      if (updatedValues !== null && "rxs" in updatedValues && !isEditing) {
        // Call handlerxTableDataChange with the updated values
        setrxTableData(updatedValues.rxs);
        handlerxTableDataChange(updatedValues?.rxs);
        setUpdatedValues(null); // Reset updatedValues
      }
    }, [updatedValues]);

    useEffect(() => {
      if (isEditing && selectedPatientId) {
        const fetchData = async () => {
          fetchAndFormatPatientData(selectedPatientId);
        };

        fetchData();
        // setShowFields(true);

        // dummyApiFetch().then((data) => {
        //   setValues(data);
        // });
      }
      // Make API request to fetch patient data based on patient ID or any other identifier
      else {
        setValues(initialFValues);
        handleAddAddress();
      }
    }, [isEditing]); // Before it was [isEditing, filteredValue]

    const handleQuickAddSave = () => {
      // Build a single Rx row from quickRxData and its tableData
      const newRxRow = {
        ...quickRxData.tableData[0], // Use the first (or only) row as base
        accountId: quickRxData.rxAccount,
        rxtype: quickRxData.rxTypeData.id,
        tarifGroupId: quickRxData.selectedTarifGroup,
        id: uuidv4(),
        therapyRxs: quickRxData.tableData, // Store all therapies for this Rx
      };

      setrxTableData((prevRxTableData: any[]) => {
        const existingIndex = prevRxTableData.findIndex(
          (item) => item.id === newRxRow.id,
        );
        let updatedRxTableData = [...prevRxTableData];

        if (existingIndex !== -1) {
          // Update existing Rx row
          updatedRxTableData[existingIndex] = newRxRow;
        } else {
          // Add new Rx row
          updatedRxTableData.push(newRxRow);
        }

        // Optionally, call handlerxTableDataChange here with updatedRxTableData
        handlerxTableDataChange(updatedRxTableData);

        return updatedRxTableData;
      });

      handleEditClose();
    };

    // const handleQuickAddSave = () => {
    //   console.log("jjkskskksk",quickRxData)
    //   setrxTableData((prevRxTableData: any[]) => {
    //     let updatedRxTableData = [...prevRxTableData];

    //     quickRxData.tableData.forEach((newRow: any) => {
    //       // Check if the row already exists by id
    //       const existingIndex = updatedRxTableData.findIndex((item) => item.id === newRow.id);

    //       if (existingIndex !== -1) {
    //         // Update existing row
    //         updatedRxTableData[existingIndex] = newRow;
    //       } else {
    //         // Add new row (ensure it has an id)
    //         updatedRxTableData.push({
    //           ...newRow,
    //           accountId: quickRxData.rxAccount,
    //           rxtype:quickRxData.rxTypeData.id,
    //           tarifGroupId: quickRxData.selectedTarifGroup,
    //           id: newRow.id || uuidv4(), // assign a new id if missing
    //         });
    //       }
    //     });

    //     return updatedRxTableData;
    //   });
    // handlerxTableDataChange(rxtableData);
    // handleEditClose()
    // };

    const validateAddressObj = (address: AddressDto): AddressDtoValidation => {
      let validationObj: AddressDtoValidation = {
        ...address,
        id: address.id!,
        contactInfo: {},
      };

      let contactInfoValidation: Optional<ContactInfoDTO> = {};
      //Meldeadresse check
      if (
        address.isPrimary &&
        !address.contactInfo?.mobile &&
        !address.contactInfo?.phone1
      ) {
        contactInfoValidation["mobile"] = "telefonnummer ist erforderlich";
      }

      // Meldeadresse: street and city are required
      if (address.isPrimary) {
        if (!address.contactInfo?.addressLine1?.trim()) {
          contactInfoValidation["addressLine1"] = "Pflichtfeld";
        }
        if (!address.contactInfo?.city?.trim()) {
          contactInfoValidation["city"] = "Pflichtfeld";
        }
      }

      //Rechnungsaddresse
      if (address.isAlternativeBilling) {
        contactInfoValidation["addressLine1"] = address?.contactInfo
          ?.addressLine1
          ? ""
          : "Pflichtfeld";
        contactInfoValidation["zip"] = address?.contactInfo?.zip
          ? ""
          : "Pflichtfeld";
        contactInfoValidation["city"] = address?.contactInfo?.city
          ? ""
          : "Pflichtfeld";

        //Full name
        // if (!address.isPrimary) {
        contactInfoValidation["salutation"] = address?.contactInfo?.salutation
          ? ""
          : "Pflichtfeld";
        contactInfoValidation["lastName"] = address?.contactInfo?.lastName
          ? ""
          : "Pflichtfeld";
        contactInfoValidation["firstNames"] = address?.contactInfo?.firstNames
          ? ""
          : "Pflichtfeld";
        // }
      }

      //Anfahrtsaddresse
      if (
        address.isVisit &&
        !(address?.contactInfo?.zip && address.contactInfo?.addressLine1)
      ) {
        contactInfoValidation["zip"] = address.contactInfo?.zip
          ? ""
          : "Pflichtfeld";
        contactInfoValidation["addressLine1"] = address.contactInfo
          ?.addressLine1
          ? ""
          : "Pflichtfeld";
      }

      if (
        address.type == AddressDtoTypeEnum.Heim &&
        !address?.contactInfo?.businessName?.trim()
      ) {
        contactInfoValidation["businessName"] = address.contactInfo
          ?.businessName
          ? ""
          : "Pflichtfeld";
      }

      // If zip is provided, it must be exactly 5 digits
      const zip = address?.contactInfo?.zip?.trim();
      if (zip && !/^\d{5}$/.test(zip)) {
        contactInfoValidation["zip"] = "PLZ muss 5 Ziffern haben";
      }

      validationObj.contactInfo = contactInfoValidation;

      return validationObj;
    };
    const getAddressesValidationErrors = (
      addresses: AddressDto[],
    ): (AddressDtoValidation | null)[] => {
      let addressesErrors: (AddressDtoValidation | null)[] = [];
      for (let i = 0; i < addresses.length; i++) {
        let address = addresses[i];
        let errorObj = validateAddressObj(address);
        //Check if all values in the contactInfo in errorObj object are empty strings
        const isValidContactInfo = Object.values(errorObj.contactInfo).every(
          (x) => x === "",
        );
        // if (!isValidContactInfo) {
        //   addressesErrors.push(errorObj);
        // } else {
        //   addressesErrors.push(null);
        // }
        addressesErrors.push(errorObj);
      }

      return addressesErrors;
    };
    const validate = (fieldValues = values) => {
      let temp: any = { ...errors };
      if ("firstName" in fieldValues)
        temp.firstName = fieldValues.firstName ? "" : "Pflichtfeld";
      if ("lastName" in fieldValues)
        temp.lastName = fieldValues.lastName ? "" : "Pflichtfeld";
      if ("dob" in fieldValues) temp.dob = fieldValues.dob ? "" : "Pflichtfeld";
      // if ("payer" in fieldValues)
      //   temp.payer = fieldValues.payer ? "" : "Pflichtfeld";
      if ("tarifGroupId" in fieldValues)
        temp.tarifGroupId = fieldValues.tarifGroupId ? "" : "Pflichtfeld";
      // if ("insuranceNo" in fieldValues)
      //   temp.insuranceNo = fieldValues.insuranceNo
      //     ? ""
      //     : "This field is required.";
      if ("coPayment" in fieldValues) {
        temp.coPayment = fieldValues.coPayment !== null ? "" : "Pflichtfeld";
      }
      // Done by Nafees
      const hasPartialExemptions = (fieldValues.copaymentExemptions || []).some(
        (exemption: CopaymentExemptionFormValue) =>
          (exemption.startDate && !exemption.endDate) ||
          (!exemption.startDate && exemption.endDate),
      );
      temp.copaymentExemptions = hasPartialExemptions
        ? "Bitte Start- und Enddatum gemeinsam ausfüllen."
        : "";

      // Validate chipcard (if chipcard exists in fieldValues)
      if ("chipcard" in fieldValues) {
        const chipCardValue = fieldValues.chipcard;
        if (
          chipCardValue &&
          !insuranceOptions?.some((option: any) => option.ik === chipCardValue)
        ) {
          temp.chipcard = "Ungültige Chipkartennummer";
        } else {
          temp.chipcard = ""; // Clear error if valid
        }
      }

      // if ("personType" in fieldValues)
      // temp.personType = fieldValues.personType ? "" : "This field is required.";

      // if ('email' in fieldValues)
      //     temp.email = (/$^|.+@.+..+/).test(fieldValues.email) ? "" : "Email is not valid."

      // if ("mobile" in fieldValues || "phone1" in fieldValues || "phone2" in fieldValues) {
      //   temp.contact =
      //     fieldValues.mobile || fieldValues.phone1 || fieldValues.phone2 ? (
      //       ""
      //     ) : (
      //       <Typography sx={{ fontSize: "10px" }}>
      //         "Mindestens ein Kontakt (Mobiltelefon, Tel. 1, Tel. 2) ist erforderlich."
      //       </Typography>
      //     );
      // }

      setErrors({
        ...temp,
      });

      //Address validation
      const addressesErrors = getAddressesValidationErrors(values?.addresses);
      for (let i = 0; i < addressesErrors.length; i++) {
        let addressErrorObj = addressesErrors[i];
        if (addressErrorObj?.contactInfo) {
          if (
            !Object.values(addressErrorObj.contactInfo).every((x) => x === "")
          ) {
            return false;
          }
        }
      }

      // Check if all values in the temp object are empty strings (valid)
      const isValid = Object.values(temp).every((x) => x === "");

      return isValid;
      // if (fieldValues == values) return Object.values(temp).every((x) => x == "");
    };
    const [unsavedChangesWarning, setShowunsavedChangesWarning] =
      useState(false);
    const [unsavedChanges, setUnsavedChanges] = useState(false);
    const handleClosePatient = () => {
      if (unsavedChanges) {
        // If there are unsaved changes, set a state to open the warning dialog
        setShowunsavedChangesWarning(true);
      } else {
        onClosePatientDialog();
      }
    };

    const handleEditClose = () => {
      setQuickRxData({
        rxTypeData: { id: "", text: "" },
        selectedTarifGroup: "",
        rxAccount: "",
        tableData: [],
      });

      setIsQuickAdd(false);
    };

    const handleUnsavedChangesWarning = () => {
      handleSubmit();
      setShowunsavedChangesWarning(false);
    };

    const handleCloseUnsavedChangesWarning = () => {
      onClosePatientDialog();
      // If user cancels and chooses not to continue, close the warning dialog
      setShowunsavedChangesWarning(false);
    };

    const {
      values,
      setValues,
      errors,
      setErrors,
      handleInputChange,
      resetForm,
    } = useForm(initialFValues, true, validate, setUnsavedChanges);
    useEffect(() => {
      if (values?.addresses?.length) {
        let addressesErrors = getAddressesValidationErrors(values.addresses);

        setAddressesFormValidationErrors(addressesErrors);
        // setAddressesFormValidationErrors((prevFormValues: any) => ({
        //   ...prevFormValues,
        //   ...addressesErrors,
        // }));
      }
    }, [values?.addresses]);

    // Done by Nafees
    const handleAddCopaymentExemption = () => {
      setUnsavedChanges(true);
      const currentYear = new Date().getFullYear();
      const startDate = `${currentYear}-01-01`;
      const endDate = `${currentYear}-12-31`;

      setValues((prevFormValues: any) => ({
        ...prevFormValues,
        copaymentExemptions: [
          ...(prevFormValues.copaymentExemptions || []),
          {
            id: uuidv4(),
            startDate,
            endDate,
            documentId: null,
            documentName: null,
          },
        ],
      }));
    };

    const handleRemoveCopaymentExemption = (id: string) => {
      setUnsavedChanges(true);
      setValues((prevFormValues: any) => ({
        ...prevFormValues,
        copaymentExemptions: (prevFormValues.copaymentExemptions || []).filter(
          (exemption: CopaymentExemptionFormValue) => exemption.id !== id,
        ),
      }));
    };

    const handleCopaymentExemptionChange = (
      id: string,
      field: keyof Pick<CopaymentExemptionFormValue, "startDate" | "endDate">,
      value: string | null,
    ) => {
      setUnsavedChanges(true);
      setValues((prevFormValues: any) => ({
        ...prevFormValues,
        copaymentExemptions: (prevFormValues.copaymentExemptions || []).map(
          (exemption: CopaymentExemptionFormValue) =>
            exemption.id === id
              ? { ...exemption, [field]: value || "" }
              : exemption,
        ),
      }));
    };

    const handleSetExemptionYear = (exemptionId: string, year: number) => {
      setUnsavedChanges(true);
      const startDate = `${year}-01-01`;
      const endDate = `${year}-12-31`;

      setValues((prevFormValues: any) => ({
        ...prevFormValues,
        copaymentExemptions: (prevFormValues.copaymentExemptions || []).map(
          (exemption: CopaymentExemptionFormValue) =>
            exemption.id === exemptionId
              ? { ...exemption, startDate, endDate }
              : exemption,
        ),
      }));
    };

    const handleDocumentUploadSuccess = (
      exemptionId: string,
      documentId: string,
      documentName: string,
    ) => {
      setUnsavedChanges(true);
      setValues((prevFormValues: any) => ({
        ...prevFormValues,
        copaymentExemptions: (prevFormValues.copaymentExemptions || []).map(
          (exemption: CopaymentExemptionFormValue) =>
            exemption.id === exemptionId
              ? { ...exemption, documentId, documentName }
              : exemption,
        ),
      }));
    };

    // if (values?.addresses.length) {
    //   values.addresses = values.addresses.sort((a: AddressDto, b: AddressDto) => {
    //     return a.id!.localeCompare(b.id!); // Compare ids lexicographically
    //   });
    // }

    const handlePayerChange = (e: any) => {
      const { name, value, id } = e.target;
      // console.log("TArget", e.target, name);
      if (name === "payer") {
        // Autopopulate doctor and bsnr based on LANR selection

        const selectedPayer: any = insuranceOptions?.find(
          (option: any) => option.id === id || option.ik === value,
        );
        if (selectedPayer) {
          setValues((prevFormValues: any) => ({
            ...prevFormValues,
            payer: selectedPayer,
            chipcard: selectedPayer?.ik,
            tarifGroupId: selectedPayer?.tarifGroup?.id,
          }));
          setSelectedChipCard(selectedPayer.ik);
        }
      }
    };
    // console.log("SEKKDJFKKK", selectedRow, rxtableData);

    const handleSubmit = async (event?: any) => {
      if (event) {
        event.preventDefault(); // Prevent default form submission behavior
      }
      // console.log("PostData", updatedValues);

      try {
        setLoading(true);
        const validatee = validate();
        if (validate()) {
          const updatedValues = {
            ...values,
            location: values.location ? values.location : currentLocation,
            payer:
              insuranceOptions?.find(
                (option: any) => option.name === values.payer,
              )?.id || values.payer,
            // Stelle sicher, dass dataPrivacyPolicyStatus immer einen Wert hat
            dataPrivacyPolicyStatus:
              values.dataPrivacyPolicyStatus ||
              DataPrivacyPolicyStatus.NOT_INFORMED,
          };

          // Use React Query mutation instead of direct store method
          await createOrUpdatePatientMutation.mutateAsync(
            updatedValues as unknown as PatientDto,
            {
              onSuccess: (response) => {
                setUnsavedChanges(false);
                if (isEditing) {
                  //TO CHECK why it was added
                  fetchAndFormatPatientData(selectedPatientId!);
                  onClosePatientDialog();
                } else {
                  // setActiveTab(response);
                  // Update the state to activate the newly added patient's tab
                  setValues(initialFValues as any);
                  setrxTableData([]);
                  navigateToPatient(navigate, response.patient.id, null);
                }
                // alert("Patient is added successfully");
              },
              // onError: (error) => {
              //   console.log("Error adding patient:", error);
              //   notificationStore.showMessage(error?.response?.data?.message || "Fehler beim Hinzufügen des Patienten", "error");
              // },
            },
          );
        } else {
          notificationStore.showMessage(
            "Bitte füllen Sie alle erforderlichen Felder aus",
            "warning",
          );
        }
      } catch (error) {
        // console.log("Error adding patient:", error);
      } finally {
        // Set loading back to false when the API call is complete
        setLoading(false);
      }
    };

    const handleChipCardDataEntry = (
      event: React.KeyboardEvent | React.FocusEvent,
    ) => {
      const chipCardValue = (event.currentTarget as HTMLInputElement).value;
      if (
        (event as React.KeyboardEvent).key === "Enter" ||
        event.type === "blur" ||
        event.type === "mouseleave"
      ) {
        // Check if the chipcard number is valid
        const selectedPayer = insuranceOptions?.find(
          (option: any) => option.ik === chipCardValue,
        );
        if (selectedPayer) {
          // If valid, autofill payer info
          setValues((prevFormValues: any) => ({
            ...prevFormValues,
            payer: selectedPayer,
            tarifGroupId: selectedPayer?.tarifGroup?.id,
          }));
          notificationStore.showMessage(
            `${selectedPayer?.id} ist ein IK für ${selectedPayer?.name}`,
            "success",
          );
        } else if (chipCardValue) {
          setValues((prevFormValues: any) => ({
            ...prevFormValues,
            payer: null,
          }));
          // Show error if chipcard is invalid
          notificationStore.showMessage(
            "Bitte geben Sie eine gültige Chipkartennummer ein.",
            "warning",
          );
          // Pass all form values with updated chipcard value for validation
        } else {
          setValues((prevFormValues: any) => ({
            ...prevFormValues,
            payer: null,
          }));
        }
      }
    };

    // const handleSubmit = (e: any) => {
    //   e.preventDefault();
    //   if (validate()) {
    //     // employeeService.insertEmployee(values)
    //     resetForm();
    //   }
    // };

    const handleCheckboxChange = (event: any) => {
      const { name, value } = event.target;
      setValues((prevValues: any) => ({
        ...prevValues,
        [name]: value,
      }));
    };
    const [typedTherapy, setTypedTherapy] = useState("");
    const [parsedData, setParsedData] = useState({});

    const initialColumns: any[] = [
      // { field: "RezNum", headerName: "RezNum", width: 150 },
      // {
      //   field: "date",
      //   headerName: "Rezept Date",
      //   width: 150,
      //   // type: "date",
      // },
      {
        field: "Actions",
        width: 70,
        renderCell: (params: any) => (
          <>
            <IconButton onClick={() => handleRowDoubleClick(params.row)}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => handleDeleteRx(params.row)}>
              <Delete />
            </IconButton>
          </>
        ),
      },
      { field: "creationDate", headerName: "Creation" },
      {
        field: "rxtype",
        headerName: "Rezept-typ",
        width: 160,
      },
      {
        field: "tarifGroupId",
        headerName: "Tarif Group",
        width: 140,
      },
      {
        field: "accountId",
        headerName: "Mandant",
        width: 140,
      },
      { field: "visitType", headerName: "Location", editable: true },
      // { field: "therapy", headerName: "Heilmittel", editable: true },
      { field: "amount", headerName: "Menge", editable: true },
      { field: "frequency", headerName: "Frequency", editable: true },
    ];
    const quickRxColumns: any[] = [
      // { field: "RezNum", headerName: "RezNum", width: 150 },
      // {
      //   field: "date",
      //   headerName: "Rezept Date",
      //   width: 150,
      //   // type: "date",
      // },
      {
        field: "Actions",
        width: 70,
        renderCell: (params: any) => (
          <>
            <IconButton onClick={() => handleRowDoubleClick(params.row)}>
              <Edit />
            </IconButton>
            <IconButton onClick={() => handleDeleteRx(params.row)}>
              <Delete />
            </IconButton>
          </>
        ),
      },
      { field: "creationDate", headerName: "Creation" },
      { field: "visitType", headerName: "Location", editable: true },
      // { field: "therapy", headerName: "Heilmittel", editable: true },
      { field: "amount", headerName: "Menge", editable: true },
      { field: "frequency", headerName: "Frequency", editable: true },
    ];
    const [columnSets, setColumnSets] = useState<any[]>([]);
    const handleAddColumnSet = () => {
      const newColumnSet = generateColumnSet(columnSets.length + 1);
      setColumnSets((prevColumnSets) => [...prevColumnSets, newColumnSet]);
    };

    useEffect(() => {
      if (isQuickAdd) {
      }
    }, [isQuickAdd]);
    // Function to generate a new column set
    const generateColumnSet = (columnIndex: any) => [
      {
        field: `therapy${columnIndex}`,
        headerName: `Heilmittel ${columnIndex}`,
        // width: 150,
        editable: true,
      },
      {
        field: `amount${columnIndex}`,
        headerName: `Menge ${columnIndex}`,
        // width: 150,
        editable: true,
      },
      {
        field: `frequency${columnIndex}`,
        headerName: `Frequency ${columnIndex}`,
        // width: 150,
        editable: true,
      },
    ];

    //   const columnIndex = columnSets.length + 1;

    //   const newColumnSet = [
    //     {
    //       field: `therapy${columnIndex}`,
    //       headerName: `Heilmittel ${columnIndex}`,
    //       width: 150,
    //       editable: true,
    //     },
    //     {
    //       field: `number${columnIndex}`,
    //       headerName: `Menge ${columnIndex}`,
    //       width: 150,
    //       editable: true,
    //     },
    //     {
    //       field: `frequency${columnIndex}`,
    //       headerName: `Frequency ${columnIndex}`,
    //       width: 150,
    //       editable: true,
    //     },
    //   ];

    //   setColumnSets((prevColumnSets) => [...prevColumnSets, newColumnSet]);
    // };
    const visitTypeMapping: { [key: string]: string } = {
      hm: "Heim",
      hb: "Haus",
      px: "Praxis",
      // Add more mappings as needed
    };

    const handlerxTableDataChange = (data: any) => {
      const updatedRxs = data?.map((rx: any) => {
        if (rx.visitType && visitTypeMapping.hasOwnProperty(rx.visitType)) {
          rx.visitType = visitTypeMapping[rx.visitType];
        }
        // Set isHomeVisit based on visitType
        if (rx.visitType) {
          if (rx.visitType === "Haus" || rx.visitType === "Heim") {
            rx.isHomeVisit = true;
          } else if (rx.visitType === "Praxis") {
            rx.isHomeVisit = false;
          } else {
            rx.isHomeVisit = null; // Set to null if it's neither Haus nor Praxis
          }
        }

        const existingRxIndex = values.rxs.findIndex(
          (existingRx: any) => existingRx.id === rx.id,
        );

        // const newRowAdded = Math.abs(
        //   rx.therapies?.length - columnSets.length - 1
        // );
        // console.log('ZUi',newRowAdded,rx.therapies?.length,columnSets.length)

        // // Add new column sets if needed
        // const newColumnSets = Array.from({ length: newRowAdded }, (_, index) =>
        //   generateColumnSet(columnSets.length + index + 1)
        // );
        // setColumnSets((prevColumnSets) => [...prevColumnSets, ...newColumnSets]);

        const therapyRxs: any[] = [];

        if (rx.therapyRxs) {
          // const rxs = {
          //   rxNumber: rx.rxNumber,
          //   id: rx.id,
          //   date: rx.date,
          //   number: rx.therapies[0]?.amount,
          //   therapy: rx.therapies[0]?.therapy?.abbreviation,
          //   therapyId: rx.therapies[0]?.id,
          //   frequency:rx.therapies[0]?.frequency

          // };
          // setrxTableData([rxs] as any)
          // Extract rxtype ID - handle both object {id, text} and string formats
          const rxtypeId = typeof rx?.rxtype === 'object' && rx?.rxtype !== null 
            ? rx.rxtype.id 
            : rx?.rxtype;
          const rxs: any = {
            rxNumber: rx.rxNumber,
            id: rx.id,
            creationDate: rx.creationDate,
            isHomeVisit: rx.isHomeVisit,
            visitType: rx.visitType,
            // date: rx.date,
            amount: rx.amount,
            therapy: rx.therapy,
            therapyId: rx.therapyId,
            frequency: rx.frequency,
            isDoubled: rx.isDoubled,
            rxSeries: rx.rxSeries,
            accountId: rx?.accountId,
            rxtype: rxtypeId,
            tarifGroupId: rx?.tarifGroupId,
          };
          //   number: rx.therapies[0]?.amount,
          //   therapy: rx.therapies[0]?.therapy?.abbreviation,
          //   therapyId: rx.therapies[0]?.id,
          //   frequency:rx.therapies[0]?.frequency

          rx.therapyRxs.forEach((therapy: any, index: any) => {
            if (index === 0) {
              // For the first therapy, use the existing fields
              rxs.therapyId = therapy.id;
              rxs.amount = therapy.amount;
              rxs.therapy = therapy.therapy?.abbreviation || therapy.therapy || therapy.rawText;
              rxs.frequency = therapy.frequency?.text || therapy.frequency;
              // Doubling is `unitsPerDay` since MD-232 — derive the checkbox
              // boolean from it and keep the number for the save direction.
              rxs.isDoubled = (therapy.unitsPerDay ?? (therapy.isDoubled ? 2 : 1)) > 1;
              rxs.unitsPerDay = therapy.unitsPerDay;
              rxs.rxSeries = therapy.rx?.rxSeries;
            } else {
              // For subsequent therapies, add index to the field names
              rxs[`therapyId${index}`] = therapy.id;
              rxs[`amount${index}`] = therapy.amount;
              rxs[`therapy${index}`] =
                therapy.therapy?.abbreviation || therapy.therapy || therapy.rawText;
              rxs[`frequency${index}`] =
                therapy.frequency?.text || therapy.frequency;
              rxs[`isDoubled${index}`] =
                (therapy.unitsPerDay ?? (therapy.isDoubled ? 2 : 1)) > 1;
              rxs[`unitsPerDay${index}`] = therapy.unitsPerDay;
            }
          });
          // rxs.location=rx.location
          // Set the rxTableData with the newly constructed rxs array
          // setrxTableData((prevRxTableData:any) => {
          //   const updatedRxTableData = [...prevRxTableData, rxs];
          //   console.log('Updated Rx Table Data:', updatedRxTableData);
          //   return updatedRxTableData;
          // });
          setrxTableData((prevRxTableData: any) => {
            const updatedRxTableData = [...prevRxTableData];
            const existingIndex = updatedRxTableData.findIndex(
              (item) => item.id === rxs.id,
            );

            if (existingIndex !== -1) {
              updatedRxTableData[existingIndex] = rxs;
            } else {
              updatedRxTableData.push(rxs);
            }

            return updatedRxTableData;
          });
          // setrxTableData([...rxtableData, rxs] as any);
          return rx;
        }
        for (const key in rx) {
          if (
            rx.hasOwnProperty(key) &&
            key.startsWith("therapy") &&
            !key.startsWith("therapyId")
          ) {
            const index = key.slice(key.indexOf("y") + 1);
            const therapyNumber = rx[`amount${index}`] || "";
            const therapy = rx[`therapy${index}`] || "";
            const frequency = rx[`frequency${index}`] || "";
            const therapyId = rx[`therapyId${index}`] || "";

            // Check if therapy data exists before adding
            if (therapyNumber || therapy || frequency) {
              // Match the typed text against the TERM catalog, the same way
              // the server resolves it (labels + aliases, normalized). The
              // previous lookup searched tarif-filtered BILLABLE LINE ITEMS,
              // so it missed for any real term that is not also a line-item
              // abbreviation ("KG-ZNS") and left isSupplementary undefined.
              const matchedTerm = filterPrescribedTherapyOptions(
                termOptions,
                typeof therapy === "string" ? therapy : therapy?.abbreviation ?? "",
              )[0];
              const therapyObj = {
                // MD-232: a line names the prescribed TERM as the text that was
                // typed; the server resolves it text -> alias -> term on save.
                // Without `rawText` the line arrives naming no term and the
                // server answers, correctly but opaquely, that the Heilmittel
                // "unbekannt" cannot be mapped into the tarif group. The
                // `therapy` object below is the pre-refactor client-side guess,
                // kept only because the table cells still read it.
                //
                // No client uuid for a NEW line: the id is forwarded, and a
                // made-up one makes the server treat a new line as existing.
                // Same rule as `toQuickRxWriteLine`.
                ...(therapyId ? { id: therapyId } : {}),
                amount: therapyNumber,
                rawText:
                  typeof therapy === "string" ? therapy : therapy?.abbreviation ?? null,
                therapy: {
                  id: therapy.id ? therapy.id : therapy,
                  abbreviation: therapy,
                  isSupplementary: therapy.isSupplementary
                    ? therapy.isSupplementary
                    : matchedTerm?.isSupplementary,
                },
                frequency: { id: frequency, text: frequency },
                isDoubled: rx[`isDoubled${index}`] || false,
                unitsPerDay:
                  rx[`unitsPerDay${index}`] ?? (rx[`isDoubled${index}`] ? 2 : 1),
                rxSeries: rx[`rxSeries${index}`],
              };
              therapyRxs.push(therapyObj);
            }
          }
        }
        // Extract rxtype ID - handle both object {id, text} and string formats
        const rxtypeIdValue = typeof rx?.rxtype === 'object' && rx?.rxtype !== null 
          ? rx.rxtype.id 
          : rx?.rxtype;
        // Update existing rx if found, otherwise create a new one
        if (existingRxIndex !== -1) {
          return {
            ...values.rxs[existingRxIndex],
            isHomeVisit: rx.isHomeVisit,
            visitType: rx.visitType,
            therapyRxs: therapyRxs,
            rxtype: rxtypeIdValue || null,
          };
        } else {
          return {
            rxNumber: rx.rxNumber,
            id: rx.id,
            isHomeVisit: rx.isHomeVisit,
            visitType: rx.visitType,
            creationDate: rx.creationDate,
            // date: rx.date,
            therapyRxs: therapyRxs,
            accountId: rx?.accountId,
            rxtype: rxtypeIdValue || null,
            tarifGroupId: rx?.tarifGroupId,
          };
        }
      });

      const maxTherapies = Math.max(
        ...updatedRxs.map((rx: any) =>
          rx.therapyRxs ? rx.therapyRxs.length : 0,
        ),
      );

      // Calculate the number of new column sets to add
      const newColumnSetsCount = Math.max(
        0,
        maxTherapies - columnSets.length - 1,
      );

      // Add new column sets if needed
      const newColumnSets = Array.from(
        { length: newColumnSetsCount },
        (_, index) => generateColumnSet(columnSets.length + index + 1),
      );
      setColumnSets((prevColumnSets) => [...prevColumnSets, ...newColumnSets]);
      // Check if any field other than 'id' has a value
      const haveRxs = (objects: any) => {
        for (const obj of objects) {
          let allValuesAreEmpty = true;

          for (const key in obj) {
            if (
              key !== "id" &&
              obj[key] !== undefined &&
              (!Array.isArray(obj[key]) || obj[key].length !== 0)
            ) {
              allValuesAreEmpty = false;
              break; // Exit the inner loop as soon as a non-empty value is found.
            }
          }

          if (allValuesAreEmpty) {
            return false; // Return false if all values are empty except 'id'.
          }
        }

        return true; // Return true if any object has at least one non-empty value.
      };
      const filteredRxs = updatedRxs?.filter(
        (rx: any) => rx.therapyRxs && rx.therapyRxs.length > 0,
      );

      setValues((prevValues: any) => ({
        ...prevValues,
        rxs: haveRxs(updatedRxs) ? filteredRxs : [],
      }));
    };

    // const handlerxTableDataChange = (data: any) => {
    //   console.log('Data', data);
    //   const updatedRxs = data.map((rx: any) => {

    //     const therapies: any[] = [];

    //     for (const key in rx) {
    //       const existingRxIndex = values.rxs.findIndex((existingRx: any) => existingRx.id === rx.id);
    //       if (rx.hasOwnProperty(key) && key.startsWith('number')) {
    //         const index = key.slice(6);
    //       const therapyNumber = rx[key];
    //       const therapy = rx[`therapy${index}`] || '';
    //       const frequency = rx[`frequency${index}`] || '';

    //       // Check if therapy data exists before adding
    //       if (therapyNumber || therapy || frequency) {
    //         const therapyObj = {
    //           therapyId: uuidv4(),
    //           number: therapyNumber,
    //           therapy: therapy,
    //           frequency: frequency,
    //         };
    //         therapies.push(therapyObj);
    //       }
    //     }}

    //     console.log('Data1', therapies);

    //     return {
    //       rxNumber: rx.rxNumber,
    //       id: rx.id,
    //       date: rx.date,
    //       therapies: therapies,
    //     };

    //   });

    // console.log('UPDFT',updatedRxs)
    //   setValues((prevValues: any) => ({
    //     ...prevValues,
    //     rxs: updatedRxs?updatedRxs:prevValues.rxs,
    //   }));
    // };

    // const handlerxTableDataChange = (data: any) => {
    //   console.log('Data',data)
    //   const updatedRxs = data.map((rx: any) => {
    //     const existingRxIndex = values.rxs.findIndex((existingRx: any) => existingRx.id === rx.id);

    //     if (existingRxIndex !== -1) {
    //       return {
    //         ...values.rxs[existingRxIndex],
    //         therapies: values.rxs[existingRxIndex].therapies.map((therapy: any,index: number) => {
    //           console.log('ARTZZZ',rx.therapyId,therapy.therapyId,rx,therapy,index)
    //           if (therapy.therapyId) {

    //             return {
    //               ...therapy,
    //               number: rx[`number${index}`] || rx.number,
    //             therapy: rx[`therapy${index}`] || rx.therapy,
    //             frequency: rx[`frequency${index}`] || rx.frequency,
    //             };
    //           }
    //           return therapy;
    //         }),
    //       };
    //     }

    //     const therapies: any[] = [];

    //     for (const key in rx) {
    //       if (rx.hasOwnProperty(key) && key.startsWith('number')) {
    //         const index = key.slice(6);
    //         const therapyObj = {
    //           therapyId: uuidv4(),
    //           number: rx[key],
    //           therapy: rx[`therapy${index}`] || '',
    //           frequency: rx[`frequency${index}`] || '',
    //         };
    //         therapies.push(therapyObj);
    //       }
    //     }
    //     console.log('Data1',therapies)
    //     return {
    //       rxNumber: rx.rxNumber,
    //       id: rx.id,
    //       date: rx.date,
    //       therapies: therapies,
    //     };
    //   });

    //   setValues((prevValues: any) => ({
    //     ...prevValues,
    //     rxs: updatedRxs,
    //   }));
    // };

    // const parseInput = (input:string) => {
    //   const parts:any = input.split(/\s+/);
    //   const result:any = {};
    //   let therapyIndex = 0;
    //   let mengeIndex = 0;

    //   for (let i = 0; i < parts.length; i++) {
    //     if (isNaN(parts[i])) {
    //       if (parts[i].includes('HB') || parts[i].includes('HM')) {
    //         result['location'] = parts[i];
    //       } else {
    //         const therapyKey = `therapy${therapyIndex === 0 ? '' : therapyIndex}`;
    //         result[therapyKey] = parts[i];
    //         therapyIndex++;
    //       }
    //     } else {
    //       const mengeKey = `number${mengeIndex === 0 ? '' : mengeIndex}`;
    //       result[mengeKey] = parts[i];
    //       mengeIndex++;
    //     }
    //   }

    //   return result;
    // };

    // console.log("FinalValues", values, values?.smsDesired, rxtableData);
    const initialRows: any[] = [
      {
        id: 1,
        // RezNum: "1",
        Heilmittel: "KG",
        Menge: "3",
        rezeptDate: "26.05.2023",
      },
      {
        id: 2,
        // RezNum: "2",
        Heilmittel: "KGZ",
        Menge: "5",
        rezeptDate: "27.05.2023",
      },
      // Additional rows...
    ];
    const [open, setOpen] = useState(false);

    useEffect(() => {
      if (selectedInsurerType === "Post" || selectedInsurerType === "BW") {
        setValues((prevValues: any) => ({
          ...prevValues,
          coPayment: true,
        }));
      } else if (
        selectedInsurerType === "GKV" ||
        selectedInsurerType === "Privat"
      ) {
        setValues((prevValues: any) => ({
          ...prevValues,
          coPayment: values?.coPayment,
        }));
      }
    }, [selectedInsurerType]);

    const handleToggle = () => {
      setOpen(!open);
    };
    const handleTableDataChange = (data: any[]) => {
      setValues(data);
    };

    const { data: masterTablesData } = useMasterTables();
    // Prescribed TERMS — the catalog level a prescription names (MD-232).
    // `isSupplementary` is a property of the TERM (Wärme, Kälte, Kompression),
    // not of a payer's billable line item, so it is read from here.
    const { data: termOptions = [] } = usePrescribedTherapyOptions();
    const getTarifGroupIds = React.useCallback(
      () => utilGetTarifGroupIds(masterTablesData || []) || [],
      [masterTablesData],
    );
    const [insuranceOptions, setInuranceOptions] = useState<InsurerDto[]>([]);
    const insuranceStatus = (masterTablesData || [])
      .filter((obj: any) => "insuranceStatus" in obj)
      .map((obj: any) => obj.insuranceStatus);
    const insurer = (masterTablesData || [])
      .filter((obj: any) => "insurer" in obj)
      .map((obj: any) => obj.insurer)[0];

    const filteredInsurer = () => {
      if (!insurer) return;

      const insurerOptions = insurer.filter(
        (option: any) => option.type === selectedInsurerType,
      );

      setInuranceOptions(insurerOptions);
    };

    useEffect(() => {
      filteredInsurer();
    }, [selectedInsurerType]);
    const tarifGroupsMasterData = getTarifGroupIds();
    // A patient's default tarif group is never a Blanko one — those belong to a
    // Blankoverordnung, not to the patient record. Derived from the catalog so
    // ErgoBlanko is excluded the day it exists; the hardcoded check this
    // replaced named only "Blanko" and had already let PostBlanko through.
    const blankoTarifGroupIds = getBlankoTarifGroupIds(masterTablesData);

    const [value, setValue] = React.useState("1");

    const handleChange = (event: any, newValue: any) => {
      setValue(newValue);
    };

    const handleAddressChange = (addresses: AddressDto[]) => {
      if (values) {
        // console.log("Adderesses on change", addresses);
        setValues((prevFormValues: any) => ({
          ...prevFormValues,
          addresses,
        }));
      }
    };
    const handleAddAddress = () => {
      let haveExistingAddresses = values?.addresses.length ? true : false;
      let newAddress: AddressDto = {
        id: "",
        isPrimary: !haveExistingAddresses,
        isAlternativeBilling: false,
        isVisit: false,
        type: AddressDtoTypeEnum.Haus,
      };
      let addressArr = [newAddress];
      if (haveExistingAddresses)
        addressArr = [...values?.addresses!, ...addressArr];
      handleAddressChange(addressArr);
      return addressArr;
    };

    const handleToggleChange = (value: string) => {
      // const tarfifGroupId = value === "BW" ? "GKV" : value;
      const tarfifGroupId =
        value === "BW" || value === "OTHERS" ? "GKV" : value;

      setSelectedInsurerType(value);
      setValues((prevValues: any) => ({
        ...prevValues,
        tarifGroupId: tarfifGroupId,
        chipcard: null,
        payer: null,
      }));
    };

    const FormContent = (
      <>
        <Card>
          <CardHeader
            title={values.id ? `Patient (${values.id})` : "Neuer Patient"}
          />
          <CardContent>
            <Grid
              container
              spacing={2}
              justifyContent="center"
              alignItems="stretch"
            >
              <FlatCardGridItem>
                <FlatCard title="Name und Persönliche Daten" fullHeight>
                  <Controls.Select
                    name="salutation"
                    label="Anrede"
                    value={values?.salutation}
                    onChange={handleInputChange}
                    options={[
                      { label: "Kein Titel", value: null },
                      { label: "Frau", value: "Frau" },
                      { label: "Herr", value: "Herr" },
                      { label: "Frau Dr.", value: "Frau Dr." },
                      { label: "Herr Dr.", value: "Herr Dr." },
                      { label: "Frau Dr. med.", value: "Frau Dr. med." },
                      { label: "Herr Dr. med.", value: "Herr Dr. med." },
                      { label: "Frau Prof. Dr.", value: "Frau Prof. Dr." },
                      { label: "Herr Prof. Dr.", value: "Herr Prof. Dr." },
                      {
                        label: "Frau Prof. Dr. med.",
                        value: "Frau Prof. Dr. med.",
                      },
                      {
                        label: "Herr Prof. Dr. med.",
                        value: "Herr Prof. Dr. med.",
                      },
                    ]}
                    onBlur={true}
                  />

                  <Controls.Input
                    name="lastName"
                    label="Nachname"
                    value={values?.lastName}
                    onChange={handleInputChange}
                    required
                    error={errors.lastName}
                  />
                  <Controls.Input
                    name="firstName"
                    label="Vorname"
                    value={values?.firstName}
                    onChange={handleInputChange}
                    required
                    error={errors.firstName}
                  />
                  <Controls.DatePicker
                    name="dob"
                    label="Geburtsdatum"
                    value={values?.dob}
                    required
                    onChange={handleInputChange}
                    error={errors.dob}
                  />
                  <Controls.Input
                    label="Briefliche Anrede"
                    name="letterOfAddress"
                    value={
                      values?.letterOfAddress ||
                      `${values?.salutation || ""} ${values?.lastName}`
                    }
                    onChange={handleInputChange}
                  />
                </FlatCard>
              </FlatCardGridItem>
              <FlatCardGridItem>
                <FlatCard title="Versichertendaten" fullHeight>
                  <ToggleButtonGroup
                    color="secondary"
                    value={selectedInsurerType}
                    exclusive
                    onChange={(e: any) => handleToggleChange(e.target.value)}
                    aria-label="Platform"
                  >
                    <ToggleButton value="GKV">GKV</ToggleButton>
                    <ToggleButton value="Post">Post</ToggleButton>
                    <ToggleButton value="Privat">Privat</ToggleButton>
                    <ToggleButton value="BW">Bundeswehr</ToggleButton>
                    <ToggleButton value="OTHERS">Sonstiges</ToggleButton>
                  </ToggleButtonGroup>
                  <Controls.Select
                    name="tarifGroupId"
                    label="Tarif Group"
                    id={1}
                    value={values?.tarifGroupId}
                    onChange={(e: any) => handleToggleChange(e.target.value)}
                    options={tarifGroupsMasterData
                      ?.filter(
                        (value: any) =>
                          value !== "BG" && !blankoTarifGroupIds.includes(value),
                      )
                      .map((value: any) => ({
                        label: value.toString(),
                        value: value.toString(),
                      }))}
                    required
                    error={errors.tarifGroupId}
                  />

                  {selectedInsurerType === "GKV" && (
                    <>
                      <Controls.Input
                        name="chipcard"
                        label=" Kostenträgerkennung"
                        value={values?.chipcard}
                        onChange={handleInputChange}
                        onKeyDown={handleChipCardDataEntry}
                        onBlur={handleChipCardDataEntry}
                        // onMouseLeave={handleChipCardDataEntry}

                        error={errors.chipcard}
                      />
                      <Controls.Input
                        disabled
                        name="payer"
                        label="Kostenträger"
                        value={values?.payer?.name}
                        onChange={handleInputChange}
                        style={{
                          backgroundColor: mobiliTheme.palette.grey[100],
                          borderBottom: "2px solid grey",
                        }}
                      />
                    </>
                  )}
                  {selectedInsurerType === "OTHERS" && (
                    <>
                      <Controls.Input
                        name="chipcard"
                        label=" Kostenträgerkennung"
                        value={values?.chipcard}
                        onChange={handleInputChange}
                        onKeyDown={handleChipCardDataEntry}
                        onBlur={handleChipCardDataEntry}
                        // onMouseLeave={handleChipCardDataEntry}

                        error={errors.chipcard}
                      />
                      <Controls.Input
                        disabled
                        name="payer"
                        label="Kostenträger"
                        value={values?.payer?.name}
                        onChange={handleInputChange}
                        style={{
                          backgroundColor: mobiliTheme.palette.grey[100],
                          borderBottom: "2px solid grey",
                        }}
                      />
                    </>
                  )}
                  {selectedInsurerType === "Privat" && (
                    <Controls.Input
                      disabled
                      name="payer"
                      label="Kostenträger"
                      value={values?.payer?.name}
                      onChange={handleInputChange}
                      style={{
                        backgroundColor: mobiliTheme.palette.grey[100],
                        borderBottom: "2px solid grey",
                      }}
                    />
                  )}
                  {(selectedInsurerType === "Post" ||
                    selectedInsurerType === "BW") && (
                    <Controls.Select
                      name="payer"
                      label="Kostenträger"
                      id={1}
                      value={values?.payer?.name}
                      onChange={handlePayerChange}
                      options={insuranceOptions?.map((option: any) => ({
                        label: option.ik.toString(),
                        value: option.name.toString(),
                        id: option.id.toString(),
                      }))}
                      disabled={values.tarifGroupId === "Privat"}
                    />
                  )}
                  <Controls.Select
                    name="status"
                    label="Status"
                    value={values?.status}
                    onChange={handleInputChange}
                    options={[
                      { label: "10000", value: "10000" },
                      { label: "30000", value: "30000" },
                      { label: "50000", value: "50000" },
                    ]}
                    disabled={
                      selectedOption === "Privat versicherter Patient" ||
                      selectedOption === "BG-Patient"
                    } // Update this line
                  />
                  <Controls.Input
                    name="insuranceNo"
                    label="Versichertennummer"
                    value={values?.insuranceNo}
                    onChange={handleInputChange}
                    error={getKvnrError(values?.insuranceNo)}
                  />

                  <div style={{ marginTop: "20px" }}>
                    <CopaymentExemptionForm
                      patientId={selectedPatientId!}
                      exemptions={values?.copaymentExemptions || []}
                      errors={errors?.copaymentExemptions}
                      onAdd={handleAddCopaymentExemption}
                      onRemove={handleRemoveCopaymentExemption}
                      onChange={handleCopaymentExemptionChange}
                      onSetYear={handleSetExemptionYear}
                      onDocumentUploadSuccess={handleDocumentUploadSuccess}
                    />
                  </div>
                  <Typography
                    variant="subtitle2"
                    style={{ marginTop: "10px", marginBottom: "5px" }}
                  >
                    Datenschutzerklärung
                  </Typography>
                  <Controls.RadioGroup
                    name="dataPrivacyPolicyStatus"
                    value={
                      values?.dataPrivacyPolicyStatus ||
                      DataPrivacyPolicyStatus.NOT_INFORMED
                    }
                    onChange={handleInputChange}
                    options={[
                      {
                        id: DataPrivacyPolicyStatus.NOT_INFORMED,
                        label: "Nicht informiert",
                      },
                      {
                        id: DataPrivacyPolicyStatus.INFORMED_VIA_POSTER,
                        label: "Informiert durch Hinweis auf Aushang/Website",
                      },
                      {
                        id: DataPrivacyPolicyStatus.SIGNED,
                        label: "Datenschutzerklärung unterschrieben",
                      },
                    ]}
                  />
                </FlatCard>
              </FlatCardGridItem>

              <Grid item xs={12} md={12} xl={12}>
                {!isEditing && (
                  <FlatCard title="Rezepte" fullHeight>
                    <EditTable
                      initialColumns={initialColumns}
                      initialRows={rxtableData}
                      columnSets={columnSets}
                      setSelectedRow={setSelectedRow}
                      setOpenDialog={setOpenDialog}
                      onTableDataChange={handlerxTableDataChange}
                      parseInput={handleParseInput}
                      handleAddColumnSet={handleAddColumnSet}
                      masterTablesData={masterTablesData}
                      selectedRx={{ tarifGroupId: values?.tarifGroupId }}
                      setIsQuickAdd={setIsQuickAdd}
                      setQuickAddInputValue={setQuickAddInputValue}
                    />
                    <QuickAddRxDialog
                      open={isQuickAdd}
                      onClose={handleEditClose}
                      onSave={handleQuickAddSave}
                      setQuickRxData={setQuickRxData}
                      rxData={quickRxData}
                      user={user}
                      gridColumns={quickRxColumns}
                      masterData={masterTablesData}
                      initialInputValue={quickAddInputValue}
                      isQuickAdd={isQuickAdd}
                    />
                  </FlatCard>
                )}
              </Grid>
            </Grid>

            <FlatCard
              title={
                <div>
                  <label>Adressen</label>
                  <Button
                    variant="contained"
                    style={{ marginLeft: "2em" }}
                    onClick={handleAddAddress}
                  >
                    Adresse hinzufügen
                  </Button>
                </div>
              }
              // fullHeight
              sxContent={{
                padding: 0,
                margin: 0,
                width: "100%",
                backgroundColor: "none",
                border: "none",
              }}
            >
              <PatientAddress
                ref={patientAddressRef}
                patientId={selectedPatientId?.toString()!}
                patientData={values}
                addresses={values?.addresses}
                onChange={(addresses: AddressDto[]) => {
                  handleAddressChange(addresses);
                }}
                addressesFormValidationErrors={addressesFormValidationErrors}
              />
            </FlatCard>

            {/* Opens a dialog Box for receipt data entry */}
            {openDialog && (
              <ReceiptDataEntryDialog
                isEditing={isEditing}
                openDialog={openDialog}
                handleCloseDialog={handleCloseDialog}
                selectedRow={selectedRow}
                patientValues={values}
                handleData={setValues}
                patientForm={true}
                rxTableDataChange={handlerxTableDataChange}
              />
            )}
          </CardContent>
          <CardActions>
            {!useDialog && (
              <Button
                type="submit"
                disabled={loading || createOrUpdatePatientMutation.isPending}
                onClick={handleSubmit}
              >
                Erstellen
              </Button>
            )}
          </CardActions>
        </Card>
        <Modal
          open={loading || createOrUpdatePatientMutation.isPending}
          aria-labelledby="loading-modal-title"
          aria-describedby="loading-modal-description"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div>
            <CircularProgress size={60} />
          </div>
        </Modal>
        {unsavedChangesWarning && (
          <WarningDialog
            open={true} // Ensure the warning dialog is open
            onClose={handleCloseUnsavedChangesWarning} // Function to close the dialog
            onContinue={handleUnsavedChangesWarning} // Function to continue despite unsaved changes
            title="Änderungen speichern?"
            content="Du hast einige Änderungen gemacht. Möchtest du diese speichern, bevor du schließt?"
            isConfirmSave
          />
        )}
      </>
    );

    return useDialog && openPatientDialog ? (
      <Grid container>
        <TopLevelPaper>
          <Dialog
            PaperComponent={TopLevelPaper}
            open={openPatientDialog}
            onClose={handleClosePatient}
            maxWidth="xl"
            fullScreen
          >
            {/* <DialogTitle>
        Patient {values.id ? `(${values.id})` : ""}
      </DialogTitle> */}
            <DialogContent>{FormContent}</DialogContent>
            <DialogActions>
              <Button variant="text" onClick={handleClosePatient}>
                Abbrechen
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={loading || createOrUpdatePatientMutation.isPending}
                variant="contained"
              >
                Speichern
              </Button>
            </DialogActions>
          </Dialog>
        </TopLevelPaper>
      </Grid>
    ) : (
      <Grid container sx={{ justifyContent: "center" }}>
        <Grid item xs={12} xl={7.5}>
          {FormContent}
        </Grid>
      </Grid>
    );
  },
);
export default PatientPage;
